import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import PublicHeader from '../components/PublicHeader';
import { locName } from '../lib/constants';

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ phase: 'polling', attempts: 0 });
  const [data, setData] = useState(null);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: 'error', attempts: 0 });
      return;
    }
    let attempts = 0;
    const MAX = 8;
    const INTERVAL = 2000;

    async function poll() {
      if (stoppedRef.current) return;
      attempts += 1;
      try {
        const { data: r } = await api.get(`/payments/status/${sessionId}`);
        setData(r);
        if (r.payment_status === 'paid') {
          setState({ phase: 'paid', attempts });
          return;
        }
        if (r.status === 'expired') {
          setState({ phase: 'expired', attempts });
          return;
        }
        if (attempts >= MAX) {
          setState({ phase: 'timeout', attempts });
          return;
        }
        setState({ phase: 'polling', attempts });
        setTimeout(poll, INTERVAL);
      } catch (e) {
        setState({ phase: 'error', attempts });
      }
    }
    poll();
    return () => {
      stoppedRef.current = true;
    };
  }, [sessionId]);

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC' }}>
      <PublicHeader />
      <div style={{ maxWidth: 640, margin: '60px auto', padding: 24 }}>
        {state.phase === 'polling' && (
          <div data-testid="payment-polling" className="card fade-up" style={{ padding: 36, textAlign: 'center' }}>
            <div className="spin" style={{ width: 56, height: 56, border: '6px solid #E2E8F0', borderTopColor: '#0D1B3E', borderRadius: '50%', margin: '0 auto' }} />
            <h2 style={{ fontSize: 22, fontWeight: 900, color: '#0D1B3E', marginTop: 20 }}>Confirming your payment…</h2>
            <p style={{ color: '#64748B', marginTop: 8, fontSize: 13 }}>This usually takes a few seconds. Don&apos;t close this tab.</p>
            <p style={{ color: '#94A3B8', marginTop: 12, fontSize: 11 }}>Attempt {state.attempts}/8</p>
          </div>
        )}

        {state.phase === 'paid' && data?.booking && (
          <div data-testid="payment-success" className="card fade-up" style={{ padding: 32, textAlign: 'center' }}>
            <div style={{ fontSize: 56 }}>✅</div>
            <h2 style={{ fontSize: 26, fontWeight: 900, marginTop: 12, color: '#0D1B3E' }}>Payment successful</h2>
            <p style={{ color: '#64748B', marginTop: 8 }}>
              Confirmation #<strong data-testid="success-booking-id">{data.booking.id}</strong> · {data.booking.vehicle_label}
            </p>
            <div style={{ marginTop: 22, padding: 18, background: '#F1F5F9', borderRadius: 12, textAlign: 'left' }}>
              <Row label="Pickup" value={`${locName(data.booking.pickup_location)} · ${data.booking.pickup_date}`} />
              <Row label="Drop-off" value={`${locName(data.booking.dropoff_location)} · ${data.booking.dropoff_date}`} />
              <Row label="Days" value={`${data.booking.days} × $${data.booking.price_per_day.toFixed(2)}`} />
              <Row label="Deposit (refundable)" value={`$${data.booking.deposit.toFixed(2)}`} />
              <Row label="Paid" value={`$${(data.amount_total / 100).toFixed(2)} ${data.currency.toUpperCase()}`} bold />
            </div>
            <p style={{ color: '#64748B', fontSize: 13, marginTop: 20 }}>
              A receipt has been sent to <strong>{data.booking.customer_email}</strong>.
            </p>
            <p style={{ color: '#94A3B8', fontSize: 12, marginTop: 8 }}>
              After your trip, you can{' '}
              <Link to={`/review/${data.booking.id}?email=${encodeURIComponent(data.booking.customer_email)}`} style={{ color: '#E63946', fontWeight: 700 }}>
                leave a review
              </Link>{' '}
              to help other travelers.
            </p>
            <Link to="/" className="btn-primary" style={{ marginTop: 22, display: 'inline-block', textDecoration: 'none' }} data-testid="success-home-btn">
              Back to fleet
            </Link>
          </div>
        )}

        {(state.phase === 'expired' || state.phase === 'timeout' || state.phase === 'error') && (
          <div data-testid="payment-failed" className="card fade-up" style={{ padding: 36, textAlign: 'center' }}>
            <div style={{ fontSize: 56 }}>⚠️</div>
            <h2 style={{ fontSize: 22, fontWeight: 900, color: '#991B1B', marginTop: 12 }}>
              {state.phase === 'expired' ? 'Session expired' : state.phase === 'timeout' ? 'Still processing' : 'Something went wrong'}
            </h2>
            <p style={{ color: '#64748B', marginTop: 8, fontSize: 14 }}>
              {state.phase === 'timeout'
                ? 'Your payment is taking longer than usual. You will receive a confirmation email shortly.'
                : 'Please try again or contact support if the issue persists.'}
            </p>
            {sessionId && (
              <p style={{ color: '#94A3B8', marginTop: 6, fontSize: 11 }}>
                Reference: <code style={{ background: '#F1F5F9', padding: '2px 6px', borderRadius: 4 }}>{sessionId.slice(-16)}</code>
              </p>
            )}
            <div style={{ marginTop: 22, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              {state.phase === 'timeout' && (
                <button
                  onClick={() => window.location.reload()}
                  className="btn-primary"
                  data-testid="retry-status-btn"
                >
                  Check again
                </button>
              )}
              <Link to="/" className="btn-ghost" style={{ textDecoration: 'none' }}>
                Back to fleet
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 0', fontSize: 13 }}>
      <span style={{ color: '#64748B', fontWeight: bold ? 800 : 500 }}>{label}</span>
      <span style={{ color: '#0F172A', fontWeight: bold ? 900 : 700, fontSize: bold ? 16 : 13 }}>{value}</span>
    </div>
  );
}
